import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { stationNetworkApi } from '../../lib/stationNetworkApi';
import { messageErreur } from '../../lib/erreurs';
import { libelle } from '../../lib/libelles';
import { formatMontant } from '../../lib/formats';
import { Aide } from '../ui/aide';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { EmptyState } from '../ui/empty-state';
import { LoadingState } from '../ui/loading-state';

/**
 * Conditions de gérance d'une station confiée à un gérant (lot 034) :
 * commission au litre, franchise de coulage (litres pour 1 000 litres vendus),
 * retenue sur le manquant au-delà de la franchise. Ce sont des montants : l'écran
 * n'est offert qu'avec le droit « montants visibles » (le serveur le contrôle aussi).
 */

const Champ = ({ id, label, aide, children }) => (
  <div className="grid gap-1.5">
    <label htmlFor={id} className="flex items-center gap-1 text-sm font-medium text-foreground">
      {label}
      {aide}
    </label>
    {children}
  </div>
);

const nombreOuVide = (valeur) => (valeur === '' || valeur === null || valeur === undefined ? undefined : Number(valeur));

const StationManagementSettings = ({ station, peutEcrire }) => {
  const stationId = station?.id;
  const [conditions, setConditions] = useState(null);
  const [form, setForm] = useState({ commission_per_liter: '', coulage_allowance_per_1000: '', withhold_shortage: false });
  const [chargement, setChargement] = useState(false);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (!stationId) return undefined;
    let actif = true;
    setChargement(true);
    stationNetworkApi.management
      .get(stationId)
      .then((resultat) => {
        if (!actif) return;
        setConditions(resultat);
        const valeurs = resultat?.management || {};
        setForm({
          commission_per_liter: valeurs.commission_per_liter ?? '',
          coulage_allowance_per_1000: valeurs.coulage_allowance_per_1000 ?? '',
          withhold_shortage: Boolean(valeurs.withhold_shortage),
        });
      })
      .catch((erreur) => {
        if (actif) toast.error(messageErreur(erreur, 'Impossible de charger les conditions de gérance'));
      })
      .finally(() => actif && setChargement(false));
    return () => {
      actif = false;
    };
  }, [stationId]);

  const changer = (cle) => (event) => setForm((avant) => ({ ...avant, [cle]: event?.target ? event.target.value : event }));

  const enregistrer = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      const resultat = await stationNetworkApi.management.update(stationId, {
        commission_per_liter: nombreOuVide(form.commission_per_liter),
        coulage_allowance_per_1000: nombreOuVide(form.coulage_allowance_per_1000),
        withhold_shortage: Boolean(form.withhold_shortage),
      });
      setConditions(resultat);
      toast.success('Conditions de gérance enregistrées');
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’enregistrer les conditions'));
    } finally {
      setEnCours(false);
    }
  };

  if (chargement && !conditions) return <LoadingState shape="readouts" />;
  if (!conditions) return null;
  if (conditions.operating_mode !== 'MANAGED') {
    return (
      <EmptyState
        variant="empty"
        title={`Station « ${libelle('station_mode', conditions.operating_mode)} »`}
        className="border-0"
      />
    );
  }

  return (
    <form onSubmit={enregistrer} className="grid max-w-3xl gap-4 rounded-xl border border-border bg-card p-4 shadow-sm sm:grid-cols-2">
      <Champ
        id="gerance-commission"
        label="Commission au litre (FCFA)"
        aide={<Aide titre="Commission au litre">La commission du gérant est le nombre de litres vendus multiplié par ce montant.</Aide>}
      >
        <Input
          id="gerance-commission"
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={form.commission_per_liter}
          onChange={changer('commission_per_liter')}
          disabled={!peutEcrire}
          className="text-right tabular-nums"
        />
      </Champ>
      <Champ
        id="gerance-franchise"
        label="Franchise de coulage (L pour 1 000 L)"
        aide={
          <Aide titre="Franchise de coulage">
            Les litres perdus que le gérant ne paie pas : litres vendus × franchise ÷ 1 000. Au-delà, le manquant est retenu.
          </Aide>
        }
      >
        <Input
          id="gerance-franchise"
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={form.coulage_allowance_per_1000}
          onChange={changer('coulage_allowance_per_1000')}
          disabled={!peutEcrire}
          className="text-right tabular-nums"
        />
      </Champ>
      <label className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm sm:col-span-2">
        <span className="flex items-center gap-1">
          Retenue sur manquant
          <Aide titre="Retenue sur manquant">
            Le manquant au-delà de la franchise est retenu sur la commission, au prix de revient du litre.
          </Aide>
        </span>
        <Switch
          checked={Boolean(form.withhold_shortage)}
          onCheckedChange={changer('withhold_shortage')}
          disabled={!peutEcrire}
          aria-label="Retenue sur manquant"
        />
      </label>
      <div className="flex items-center justify-between gap-3 sm:col-span-2">
        <span className="flex items-center gap-2 text-sm text-muted-foreground tabular-nums">
          {form.commission_per_liter !== '' && `Commission : ${formatMontant(form.commission_per_liter, undefined, 2)} le litre`}
          {!conditions.terms_set && <Badge variant="warning">À renseigner</Badge>}
        </span>
        {peutEcrire && (
          <Button type="submit" disabled={enCours}>
            Enregistrer
          </Button>
        )}
      </div>
    </form>
  );
};

export default StationManagementSettings;
